import { useQueries } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { useOrganizationResourcesClient } from "@/features/credentials/hooks/use-organization-resources-client";
import { getListItems } from "@/features/credentials/lib/get-list-items";
import { uniqueBy } from "@/features/credentials/lib/unique-by";
import type { Entity } from "@/features/credentials/models/organization-resource";
import type { SeasonTeam, SeasonTeamGroup } from "@/features/credentials/models/season-team-tree";
import { organizationResourcesQueryKeys } from "@/features/credentials/queries/organization-resources-query-keys";
import { useSeasonsListQuery } from "@/features/credentials/queries/use-seasons-list";

const TEAM_STALE_TIME_MS = 5 * 60 * 1000;

function normalizeTeams(items: Entity[]): SeasonTeam[] {
  return uniqueBy(
    items.map((entity) => ({
      entityId: String(entity.entityId ?? ""),
      name: entity.name?.trim() ?? "",
    })),
    (entity) => entity.entityId,
  );
}

export function useSeasonTeamsTreeQuery({
  sport,
  organizationId,
  competitionIds,
  expandedSeasonIds,
  enabled,
}: {
  sport: string;
  organizationId: string;
  competitionIds: string[];
  expandedSeasonIds: string[];
  enabled: boolean;
}) {
  const { productToken } = useAuth();
  const organizationResourcesClient = useOrganizationResourcesClient();
  const seasonsQuery = useSeasonsListQuery({
    sport,
    organizationId,
    competitionIds,
    enabled,
  });
  const seasons = seasonsQuery.data ?? [];
  const expandedSeasonIdSet = new Set(expandedSeasonIds);

  const teamQueries = useQueries({
    queries: seasons.map((season) => {
      const seasonId = String(season.seasonId ?? "");
      return {
        queryKey: organizationResourcesQueryKeys.entities({
          sport,
          organizationId,
          seasonIds: [seasonId],
        }),
        queryFn: async () => {
          const response = await organizationResourcesClient.getEntities({
            productToken: productToken as string,
            sport,
            organizationId,
            seasonId,
          });
          return normalizeTeams(getListItems(response));
        },
        enabled: enabled && expandedSeasonIdSet.has(seasonId) && !!productToken && !!seasonId,
        staleTime: TEAM_STALE_TIME_MS,
      };
    }),
  });

  const groups: SeasonTeamGroup[] = seasons
    .map((season, index) => {
      const seasonId = String(season.seasonId ?? "");
      const query = teamQueries[index];
      return {
        seasonId,
        name: season.name?.trim() ?? "",
        teams: query?.data ?? [],
        isLoading: expandedSeasonIdSet.has(seasonId) && Boolean(query?.isFetching) && !query?.data,
      };
    })
    .filter((group) => group.seasonId);

  return {
    groups,
    isLoadingSeasons: seasonsQuery.isFetching && groups.length === 0,
  };
}
